import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, ChevronRight, X, Share2, Trash2, Send, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

/** Compact horizontal carousel of the user's saved AR Field-Team photos. */
export default function MyFieldTeamCarousel() {
  const nav = useNavigate();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // photo currently in lightbox

  const refresh = async () => {
    try {
      const { data } = await api.get('/ar/gallery', { params: { limit: 20 } });
      setPhotos(data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  if (loading) return null;

  return (
    <section data-testid="profile-field-team-carousel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-accent flex items-center gap-1.5">
            <Camera className="w-3 h-3" /> Field-Team
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5">AR photos of you with your discoveries</p>
        </div>
        <button
          type="button"
          onClick={() => nav('/ar')}
          data-testid="ft-carousel-new"
          className="text-[11px] uppercase tracking-widest text-accent hover:text-white flex items-center gap-1"
        >
          New shot <ChevronRight className="w-3 h-3" />
        </button>
      </div>

      {photos.length === 0 ? (
        <button
          type="button"
          onClick={() => nav('/ar')}
          data-testid="ft-carousel-empty"
          className="w-full rounded-2xl border border-dashed border-white/15 bg-black/20 p-5 flex flex-col items-center gap-2 hover:border-accent/50 transition"
        >
          <Camera className="w-5 h-5 text-accent" />
          <p className="text-sm text-white/80">Take your first Field-Team selfie</p>
          <p className="text-[11px] text-muted-foreground text-center max-w-[240px]">Open AR, pick your top card, and drop yourself into a photo with the creature.</p>
        </button>
      ) : (
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory">
          {photos.map((p, i) => (
            <motion.button
              key={p.photo_id}
              type="button"
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: Math.min(i, 8) * 0.03 }}
              onClick={() => setActive(p)}
              data-testid={`ft-carousel-item-${i}`}
              className="relative shrink-0 snap-start w-28 aspect-[3/4] rounded-xl overflow-hidden ring-1 ring-white/10 hover:ring-accent/60 transition"
              aria-label={`View ${p.species_name || 'field-team'} photo`}
            >
              <img src={p.picture} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent" />
              <div className="absolute inset-x-1.5 bottom-1.5">
                <p className="text-[10px] font-serif leading-tight line-clamp-1 text-white">{p.species_name || 'Wildlife'}</p>
              </div>
              {p.is_public && (
                <span className="absolute top-1.5 left-1.5 text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-emerald-500/90 text-black font-mono font-bold">Lodge</span>
              )}
            </motion.button>
          ))}
        </div>
      )}

      <AnimatePresence>
        {active && (
          <PhotoLightbox
            photo={active}
            onClose={() => setActive(null)}
            onRefresh={refresh}
          />
        )}
      </AnimatePresence>
    </section>
  );
}

function PhotoLightbox({ photo, onClose, onRefresh }) {
  const [busy, setBusy] = useState(null); // 'share' | 'post' | 'unpost' | 'delete'
  const isPublic = !!photo.is_public;

  const share = async () => {
    setBusy('share');
    try {
      const url = photo.picture.startsWith('http') ? photo.picture : `${window.location.origin}${photo.picture}`;
      if (navigator.share) {
        await navigator.share({ title: `${photo.species_name || 'Wildlife'} · WildQuest Field-Team`, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied to clipboard');
      }
    } catch { /* user cancelled */ }
    finally { setBusy(null); }
  };

  const postToLodge = async () => {
    setBusy('post');
    try {
      await api.post(`/ar/photo/${photo.photo_id}/post-to-lodge`);
      toast.success('Posted to the Lodge — visible on the Field-Team Wall');
      onRefresh?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not post');
    } finally { setBusy(null); }
  };

  const unpost = async () => {
    setBusy('unpost');
    try {
      await api.post(`/ar/photo/${photo.photo_id}/unpost`);
      toast.success('Removed from the Lodge');
      onRefresh?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not remove');
    } finally { setBusy(null); }
  };

  const del = async () => {
    if (!window.confirm('Delete this Field-Team photo? This cannot be undone.')) return;
    setBusy('delete');
    try {
      await api.delete(`/ar/photo/${photo.photo_id}`);
      toast.success('Photo deleted');
      onRefresh?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not delete');
    } finally { setBusy(null); }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[120] bg-black/90 backdrop-blur-sm flex flex-col"
      data-testid="ft-lightbox"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        data-testid="ft-lightbox-close"
        className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 z-10"
        style={{ top: 'max(env(safe-area-inset-top), 1rem)' }}
      >
        <X className="w-5 h-5" />
      </button>
      <div className="flex-1 flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
        <img src={photo.picture} alt="" className="max-w-full max-h-full rounded-xl shadow-2xl" />
      </div>
      <div className="p-4 pb-[max(env(safe-area-inset-bottom),1rem)]" onClick={(e) => e.stopPropagation()}>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <button
            onClick={share}
            disabled={!!busy}
            data-testid="ft-lightbox-share"
            className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/10 border border-white/15 hover:bg-white/15 transition disabled:opacity-60"
          >
            {busy === 'share' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
            <span className="text-sm">Share</span>
          </button>
          {isPublic ? (
            <button
              onClick={unpost}
              disabled={!!busy}
              data-testid="ft-lightbox-unpost"
              className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/10 border border-white/15 hover:bg-white/15 transition disabled:opacity-60"
            >
              {busy === 'unpost' ? <Loader2 className="w-4 h-4 animate-spin" /> : <EyeOff className="w-4 h-4" />}
              <span className="text-sm">Remove from Lodge</span>
            </button>
          ) : (
            <button
              onClick={postToLodge}
              disabled={!!busy}
              data-testid="ft-lightbox-post"
              className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-emerald-500 text-black hover:bg-emerald-400 transition disabled:opacity-60 font-semibold"
            >
              {busy === 'post' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              <span className="text-sm">Post to Lodge</span>
            </button>
          )}
          <button
            onClick={del}
            disabled={!!busy}
            data-testid="ft-lightbox-delete"
            className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-rose-500/15 border border-rose-500/30 text-rose-200 hover:bg-rose-500/25 transition disabled:opacity-60"
          >
            {busy === 'delete' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            <span className="text-sm">Delete</span>
          </button>
        </div>
        {photo.species_name && (
          <p className="text-center text-[11px] text-muted-foreground mt-3 font-serif">
            {photo.species_name} · {new Date(photo.created_at).toLocaleDateString(undefined, { day:'numeric', month:'short', year:'numeric' })}
          </p>
        )}
      </div>
    </motion.div>
  );
}
